import {
  Button,
  createMuiTheme,
  MuiThemeProvider,
  TextField,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  deleteCustomer,
  getCustomerById,
  signUpCustomer,
  updateCustomer,
} from "../../../../api/customer";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { useSkeleton } from "../../../hooks/useSkeleton";

import BootstrapTable from "react-bootstrap-table-next";
import ToolkitProvider from "react-bootstrap-table2-toolkit";
import MultiTabBlock from "../../../components/MultiTabBlock";
import UserSection from "./UserSection";
import ProjectSection from "./ProjectSection";
import DealSection from "./DealSection";
import SubscriptionPlanSection from "./SubscriptionPlanSection";
import { CustomerTab } from "./CustomerTab";
import {
  getPlanCustomerByCustomer,
  getPlanCustomerById,
} from "../../../../api/planCustomer";

// Create theme for delete button (red)
const theme = createMuiTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyCustomer(role) {
  return {
    fullName: "",
    cif: "",
    phoneNumber: null,
    country: null,
    province: null,
    city: null,
    logoURL: "",
    website: "",
    linkedIn: "",
    description: "",
    credentials: "",
    businessArea: [],
    advisorService: [],
    active: true,
  };
}

export function NewTable({ data, columns, ...tableProps }) {
  const rowClasses = (row) => (row.active === false ? "inactive-row" : "");
  return (
    <ToolkitProvider
      bootstrap4
      keyField="id"
      data={data}
      columns={columns}
      search
    >
      {(props) => (
        <div>
          <BootstrapTable
            wrapperClasses="table-responsive"
            classes="table table-head-custom table-vertical-center overflow-hidden"
            bordered={false}
            striped
            rowClasses={rowClasses}
            {...tableProps}
            {...props.baseProps}
          />
        </div>
      )}
    </ToolkitProvider>
  );
}

export const DefaultTextField = ({
  label,
  value,
  onChange,
  required = true,
  ...props
}) => {
  return (
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      fullWidth
      margin="normal"
      variant="outlined"
      required={required}
      {...props}
    />
  );
};

export default function EditCustomerPage() {
  const customerId = useParams().id;
  const history = useHistory();
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  const rolePathSingle = useLocation()
    .pathname.split("/")[1]
    .replace("edit-", "");
  const rolePathUpperCase =
    rolePathSingle.charAt(0).toUpperCase() + rolePathSingle.slice(1);
  const rolePath = rolePathSingle + "s";

  const [customer, setCustomer] = useState(getEmptyCustomer(rolePathSingle));
  const [users, setUsers] = useState([]);
  const [planCustomer, setPlanCustomer] = useState([]);

  const [selectedLogo, setSelectedLogo] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const [descriptionRejected, setDescriptionRejected] = useState("");

  useEffect(() => {
    if (!customerId) {
      disableLoadingData();
      return;
    }

    getPlanCustomerByCustomer(customerId)
      .then((res) => {
        setPlanCustomer(
          res.data.flatMap((pC) => {
            const history = pC.history.map((pcH) => ({
              ...pcH.plan,
              startDate: pcH.startDate,
              endDate: pcH.endDate,
            }));

            const actualPlan = {
              ...pC.plan,
              startDate: pC.startDate,
              endDate: pC.endDate,
            };
            return [actualPlan, ...history.slice().reverse()];
          })
        );
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: `Could not get subscription plan`,
        });
      });

    getCustomerById(customerId)
      .then((res) => {
        setCustomer(res.data);
        setUsers(res.data?.userCustomer);

        disableLoadingData();
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: `Could not get ${rolePathSingle}`,
        });
        history.push(`/${rolePath}`);
      });
  }, [customerId, disableLoadingData, history, rolePath, rolePathSingle]);

  function customerDialog(reason) {
    //if (project.validated || (project.status === "review" && !project.validated)) {
    if (reason) {
      return (
        <TextField
          label="Explain the changes..."
          value={descriptionRejected}
          onChange={(e) => setDescriptionRejected(e.target.value)}
          fullWidth
          margin="normal"
          variant="outlined"
          multiline
          minRows={4}
          maxRows={10}
        />
      );
    }
    return null;
  }

  function saveCustomer() {
    if (!customerId) {
      const planCustomer = {
        planId: selectedPlan?.plan?._id,
        months:
          selectedPlan?.plan?.monthPrice === 0 ? "0" : selectedPlan?.months,
      };
      const newCustomer = { ...customer, role: [rolePathSingle] };

      const info = {
        customer: newCustomer,
        user: users?.[0],
        plan: planCustomer,
      };

      signUpCustomer(info)
        .then(() => {
          alertSuccess({
            title: "Saved!",
            customMessage: `${rolePathUpperCase} successfully created.`,
          });
          history.push(`/${rolePath}`);
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: `Could not save ${rolePathSingle}`,
          });
        });
    } else {
      let resultTmpDescription = "bckoff";
      if (customer.resultTmpDescription === "accept") {
        resultTmpDescription = "true";
      } else if (customer.resultTmpDescription === "reject") {
        resultTmpDescription = "false";
      }
      updateCustomer(customerId, customer, selectedLogo, resultTmpDescription)
        .then((res) => {
          alertSuccess({
            title: "Saved!",
            customMessage: "Changes successfully saved.",
          });
          history.push(`/${rolePath}`);
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  function getMultiTabColumns() {
    const columnsTab = [`${rolePathSingle}`, "Users", "Subscriptions"];

    /* if (rolePathSingle === "seller") {
       columnsTab.splice(2, 0, "Projects");
     }
 
     if (rolePathSingle === "buyer") {
       columnsTab.splice(2, 0, "Offers");
     }*/

    return columnsTab;
  }

  const renderMultiTabContent = (tabName) => {
    if (tabName === rolePathSingle) {
      return (
        <CustomerTab
          customer={customer}
          setCustomer={setCustomer}
          rolePathSingle={rolePathSingle}
          logo={{ selectedLogo, setSelectedLogo }}
          openLogo={{ openPreviewDialog, setOpenPreviewDialog }}
        />
      );
    } else if (tabName === "Users") {
      return (
        <UserSection users={users} setUsers={setUsers} customer={customer} />
      );
    } else if (tabName === "Projects") {
      return <ProjectSection customer={customer} setCustomer={setCustomer} />;
    } else if (tabName === "Offers") {
      return <DealSection customer={customer} setCustomer={setCustomer} />;
    } else if (tabName === "Subscriptions") {
      return (
        <SubscriptionPlanSection
          plans={planCustomer}
          setPlans={setPlanCustomer}
          customer={customer}
          selectedPlan={selectedPlan}
          setSelectedPlan={setSelectedPlan}
          rolePathSingle={rolePathSingle}
        />
      );
    }
  };

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        <CardHeader title={`Edit ${rolePathUpperCase}`}>
          {customer?.kybId && (
            <div className="d-flex align-items-center justify-content-center">
              <Button
                onClick={() => {
                  const link = `https://backoffice.thekyb.com/businessdetail?company_id=${customer?.kybId}`;
                  window.open(link, "_blank");
                }}
                variant="outlined"
                color="primary"
                style={{ marginLeft: "auto" }}
              >
                See KYB information
              </Button>
            </div>
          )}
        </CardHeader>
        <CardBody>
          <div className="d-flex align-item-center">
            <div className="col-12">
              <MultiTabBlock
                multiTabContent={renderMultiTabContent}
                columns={getMultiTabColumns()}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <div
        style={{ display: "flex", gap: "8px", justifyContent: "space-between" }}
      >
        <div>
          <Button
            onClick={() => history.push(`/${rolePath}`)}
            variant="outlined"
            style={{ marginRight: 20 }}
          >
            Back
          </Button>

          <Button
            onClick={() => saveCustomer()}
            variant="outlined"
            color="primary"
            style={{ marginRight: 20 }}
          >
            Save {rolePathUpperCase}
          </Button>

          {customerId && (
            <>
              <MuiThemeProvider theme={theme}>
                <Button
                  onClick={() => setOpenConfirmDialog(true)}
                  variant="outlined"
                  color="secondary"
                >
                  Delete {rolePathUpperCase}
                </Button>
              </MuiThemeProvider>

              <ConfirmDialog
                title={`Are you sure you want to delete this ${rolePathSingle}?`}
                open={openConfirmDialog}
                setOpen={setOpenConfirmDialog}
                onConfirm={() => {
                  deleteCustomer(customerId)
                    .then(() => {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: `${rolePathUpperCase} deleted successfully`,
                      });
                      history.push(`/${rolePath}`);
                    })
                    .catch((error) => {
                      alertError({
                        error: error,
                        customMessage: `Could not delete ${rolePathSingle}`,
                      });
                    });
                }}
              />
            </>
          )}
        </div>

        {rolePathSingle === "advisor" && customer.status === "review" && (
          <div style={{ display: "flex", gap: "20px" }}>
            <MuiThemeProvider theme={theme}>
              <Button
                onClick={() => setOpenConfirmDialog(3)}
                variant="outlined"
                color="secondary"
                style={{ marginLeft: "auto" }}
              >
                Reject advisor
              </Button>
            </MuiThemeProvider>
            <Button
              onClick={() => setOpenConfirmDialog(2)}
              variant="outlined"
              color="primary"
              style={{ marginLeft: "auto" }}
            >
              Accept advisor
            </Button>
          </div>
        )}
      </div>

      <ConfirmDialog
        title={`Are you sure you want to accept this advisor?`}
        open={openConfirmDialog === 2}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => {
          customer.status = "published";
          saveCustomer();
        }}
      />

      <ConfirmDialog
        title={`What changes need this customer to be accepted?`}
        open={openConfirmDialog === 3}
        setOpen={setOpenConfirmDialog}
        children={customerDialog(true)}
        DialogProps={{
          PaperProps: {
            style: customer.validated
              ? {
                  width: "50vw",
                  height: "50vh",
                  maxWidth: "none",
                }
              : {},
          },
        }}
        onConfirm={() => {
          customer.status = "draft";
          customer.descriptionRejected = descriptionRejected;
          saveCustomer();
        }}
      />
    </>
  );
}
